import {
	Box,
	Button,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { muiStyles } from "src/Utils/Styles";

import { useAuthContext } from "src/components/Context/AuthContext/AuthContext";

import {
	DatePicker,
	LocalizationProvider,
	TimePicker,
} from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { createFormData } from "nhb-toolbox";
import { ISingleFairEvent } from "src/Types/module/FairEvent/FairEventType";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useValidImage } from "src/hooks/useValidImage";

const useStyles = muiStyles;

interface IProps {
	singleEvent: ISingleFairEvent;
	handleClose: () => void;
}

const EventInfoUpdate = ({ handleClose, singleEvent }: IProps) => {
	const { user } = useAuthContext();
	const { register, handleSubmit, watch } = useForm();
	const [status, setStatus] = useState<string>("");
	const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value) {
			setStatus(e.target.value);
		}
	};

	const [imagePreview, setImagePreview] = useState<string | null>(null);

	const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
		dayjs(singleEvent.event_date)
	);

	const [timeValue, setTimeValue] = useState<{
		event_start_time: Dayjs | null;
		event_end_time: Dayjs | null;
	}>({
		event_start_time: singleEvent?.event_start_time
			? dayjs(singleEvent.event_start_time, "HH:mm:ss")
			: null,
		event_end_time: singleEvent?.event_end_time
			? dayjs(singleEvent.event_end_time, "HH:mm:ss")
			: null,
	});

	const { mutate: updateEvent, isPending } = useApiMutation<FormData, void>({
		method: "PUT",
		endPoint: `/api/fair/event/for-admin/update`,
		invalidateQueries: [
			"SingleFairEvent",
			"AllEvents",
			singleEvent.event_id,
		],
	});

	const handleUpdateEvent = (inputs: Record<string, any>) => {
		inputs.event_updated_by = user.user_admin_id;
		inputs.event_id = singleEvent?.event_id;
		inputs.event_status = status;
		inputs.event_start_time = dayjs(timeValue.event_start_time).isValid()
			? dayjs(timeValue.event_start_time).format("HH:mm:ss")
			: "";

		inputs.event_end_time = dayjs(timeValue.event_end_time).isValid()
			? dayjs(timeValue.event_end_time).format("HH:mm:ss")
			: "";

		inputs.event_date = dayjs(selectedDate).isValid()
			? dayjs(selectedDate).format("YYYY-MM-DD")
			: "";

		const formData = createFormData(inputs);

		updateEvent(formData);
		handleClose();
	};

	const imgSrc = useValidImage(singleEvent?.event_photo);

	useEffect(() => {
		if (imgSrc) {
			setImagePreview(imgSrc);
		}
	}, [imgSrc]);

	const watchedFile = watch("event_photo");

	useEffect(() => {
		if (watchedFile && watchedFile.length > 0) {
			const file = watchedFile[0];
			const url = URL.createObjectURL(file);
			setImagePreview(url);

			// Clean up the object URL when the component unmounts or file changes
			return () => URL.revokeObjectURL(url);
		}
	}, [watchedFile]);

	return (
		<Box component="form" onSubmit={handleSubmit(handleUpdateEvent)}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6} lg={6}>
					<TextField
						margin="normal"
						fullWidth
						id="event title"
						defaultValue={singleEvent?.event_title}
						label="Enter event title"
						{...register("event_title")}
						type="text"
						variant="outlined"
					/>
				</Grid>
				<Grid item xs={12} md={6} lg={6}>
					<TextField
						margin="normal"
						fullWidth
						id="event type"
						label="Enter event type"
						defaultValue={singleEvent?.event_type}
						{...register("event_type")}
						type="text"
						variant="outlined"
					/>
				</Grid>

				<Grid item xs={12} md={6} lg={6}>
					<TextField
						margin="normal"
						fullWidth
						id="event venue"
						label="Enter event venue"
						defaultValue={singleEvent?.event_venu}
						{...register("event_venu")}
						type="text"
						autoFocus
						variant="outlined"
					/>
				</Grid>
				<Grid item xs={12} md={6} lg={6} mt={2}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label="Event Date"
							value={selectedDate}
							onChange={(newValue: Dayjs | null) => {
								setSelectedDate(newValue);
							}}
							slotProps={{
								textField: {
									fullWidth: true,
									InputLabelProps: { shrink: true },
								},
							}}
						/>
					</LocalizationProvider>
				</Grid>

				<Grid item xs={12} md={6} lg={6} mt={2}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<TimePicker
							slotProps={{
								textField: {
									fullWidth: true,
									InputLabelProps: { shrink: true },
								},
							}}
							label="Start time"
							value={timeValue.event_start_time}
							onChange={(value: Dayjs | null) => {
								setTimeValue({
									...timeValue,
									event_start_time: value,
								});
							}}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={12} md={6} lg={6} mt={2}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<TimePicker
							slotProps={{
								textField: {
									fullWidth: true,
									InputLabelProps: { shrink: true },
								},
							}}
							label="End time"
							value={timeValue.event_end_time}
							onChange={(value: Dayjs | null) =>
								setTimeValue({
									...timeValue,
									event_end_time: value,
								})
							}
						/>
					</LocalizationProvider>
				</Grid>

				<Grid item xs={12} md={6} lg={6}>
					<TextField
						margin="normal"
						fullWidth
						id="file"
						label="Choose Event Photo"
						InputLabelProps={{ shrink: true }}
						type="file"
						inputProps={{ accept: "image/*" }}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							const file = e.target.files?.[0];
							if (file) {
								setImagePreview(URL.createObjectURL(file));
							}
						}}
						{...register("event_photo")}
					/>
				</Grid>
				<Grid item xs={12} md={6} lg={6} mt={1.8}>
					<FormControl fullWidth>
						<InputLabel id="demo-simple-select-label">
							Status
						</InputLabel>
						<Select
							id="outlined-required"
							value={status}
							label="Status"
							onChange={handleStatusChange}
						>
							<MenuItem value="upcoming">Upcoming</MenuItem>
							<MenuItem value="ended">Ended</MenuItem>
							<MenuItem value="rejected">Rejected</MenuItem>
						</Select>
					</FormControl>
				</Grid>

				{imagePreview && (
					<Grid item xs={12} md={6} lg={6}>
						<Box
							component="img"
							src={imagePreview}
							alt="Image Preview"
							sx={{
								display: "block",
								width: "100%", // or a fixed size like "300px"
								maxWidth: 300,
								maxHeight: 100,
								objectFit: "contain",
							}}
						/>
					</Grid>
				)}
				<Grid item lg={12} mt={2}>
					<TextField
						rows={3}
						maxRows={8}
						multiline
						defaultValue={singleEvent?.event_description}
						{...register("event_description")}
						label="Enter event description"
						fullWidth
					/>
				</Grid>
			</Grid>
			{isPending ? (
				<Button
					disabled
					fullWidth
					variant="contained"
					disableElevation
					sx={{
						mt: 3,
						mb: 2,
						py: 1,
					}}
				>
					Loading...
				</Button>
			) : (
				<Button
					type="submit"
					fullWidth
					variant="contained"
					disableElevation
					sx={{
						mt: 3,
						mb: 2,
						py: 1,
					}}
				>
					Update Event
				</Button>
			)}
		</Box>
	);
};

export default EventInfoUpdate;
